/**
 * Unlock table — what each of the 55 levels hands you.
 *
 * ## This file does not own the roster
 *
 * It used to. It listed six guns with their names, classes and unlock levels, and
 * `weapons.data.js` listed the same six guns with their names and classes. Two lists
 * of the same thing in two files is a drift generator: rename a gun in one and the
 * loadout screen quietly disagrees with the gun you are holding.
 *
 * So the weapon half of the ladder is **derived**. `weapons.data.js` is the single
 * source for a weapon's `id`, `name`, `className`, `classId`, `unlockLevel`, `rank`
 * and `ttk`; this file reads them and answers the only question that is genuinely
 * progression's: *what does level N give me*. The cosmetic half below is authored
 * here, because nothing else in the game has an opinion about it.
 *
 * Two rules the ladder is built around:
 *
 *   1. **Every level unlocks something.** A level that hands you nothing reads as a
 *      bug, not as pacing. Weapons land on 17 milestone levels; every level from 1 to
 *      55 also carries a cosmetic, so no level is ever empty regardless of what the
 *      weapon data does. Cosmetics are cheap to author and cost the runtime nothing,
 *      which is exactly why they are the right filler.
 *
 *   2. **Almost nothing is withheld wholesale.** Six weapons carry `unlockLevel: 0`
 *      and the ladder mostly gives you *more* of a class you already have. The one
 *      exception is the marksman rifle, whose first entry is a level-10 unlock — that
 *      is the weapon data's call, not this file's, and the loadout screen shows the
 *      class with a `0 / 3` count rather than hiding it, so the gap reads as a goal.
 *
 * ## Defensive by construction
 *
 * The weapon dataset is rewritten by another agent, so every read of it here is
 * optional-chained and every field has a fallback. A def missing `classId` is placed
 * by its `className`; a def missing `unlockLevel` is treated as initial; a def
 * missing entirely leaves the entry marked `unbuilt` and unequippable. Nothing in
 * this file throws over a missing field, and nothing here assumes a weapon id.
 *
 * `unlockLevel: 0` means "initial issue", not "level 0" — see `LEVEL_INITIAL`.
 */

import * as WEAPON_DATA from '../weapons/weapons.data.js';
/**
 * The weapon dataset's own working: `SCRAPED` is verbatim from the reference,
 * `DERIVED` is computed from it, `AUTHORED` is invented for this game. That three-way
 * split is the only honest answer to "where did this number come from", so the stat
 * sheet reads it rather than guessing — see `weaponFacts` at the bottom.
 *
 * Importing it adds no failure mode that is not already there: `weapons.data.js`
 * imports the same file, so if it ever goes away nothing loads regardless. Every
 * access below is still optional so a *reshaped* module degrades to "assumed".
 */
import * as WEAPON_STATS from '../weapons/weapons.stats.js';

export const MAX_LEVEL = 55;

/**
 * The sentinel `unlockLevel` for a weapon you start with. Zero rather than one so
 * "initial" is distinguishable from "unlocked by the first level-up", and so the UI
 * can label it 初期支給 / STANDARD ISSUE instead of "LEVEL 0".
 */
export const LEVEL_INITIAL = 0;

/* ---------------------------------------------------------------------------- *
 * Class metadata.
 *
 * `weapons.data.js` exports `WEAPON_CLASSES` and that is authoritative for the *set*
 * and the *order*. The blurbs below are UI copy, which is this side's business, and
 * are merged in by id — a class the weapon data invents that is not listed here still
 * appears, just without a blurb.
 * ---------------------------------------------------------------------------- */

const CLASS_META = [
  { id: 'ar', label: 'AR', name: 'ASSAULT RIFLE', blurb: 'All-round. Controllable to 60 m.' },
  { id: 'smg', label: 'SMG', name: 'SUBMACHINE GUN', blurb: 'Fastest close in. Falls apart past 25 m.' },
  { id: 'sg', label: 'SG', name: 'SHOTGUN', blurb: 'One room, one shell. Nothing past 15 m.' },
  { id: 'lmg', label: 'LMG', name: 'LIGHT MACHINE GUN', blurb: 'Suppression. Deep magazine, slow to bring up.' },
  { id: 'mr', label: 'MR', name: 'MARKSMAN RIFLE', blurb: 'Semi-auto reach. Two body shots at any range.' },
  { id: 'sr', label: 'SR', name: 'SNIPER RIFLE', blurb: 'Bolt action. One shot, or nothing.' },
];

/** Fallback placement for a def with no `classId`, matched against `className`. */
const CLASS_BY_NAME = [
  [/SNIP|BOLT/i, 'sr'],
  [/MARKS|DMR|DESIGNAT/i, 'mr'],
  [/SHOTG|BREACH/i, 'sg'],
  [/MACHINE\s*GUN|LMG|SAW/i, 'lmg'],
  [/SUB|SMG/i, 'smg'],
  [/ASSAULT|RIFLE|CARBINE/i, 'ar'],
];

/** Last-ditch placement by legacy id, for the pre-rewrite dataset. */
const CLASS_BY_ID = { ar: 'ar', smg: 'smg', shotgun: 'sg', lmg: 'lmg', dmr: 'mr', sniper: 'sr' };

/* ---------------------------------------------------------------------------- *
 * Live exports.
 *
 * These are `const` arrays filled in place rather than reassigned bindings, so an
 * importer that captured the array at module load — `Progression` does — keeps
 * seeing the current contents after a rebuild.
 * ---------------------------------------------------------------------------- */

/** Ordered class metadata: `{ id, label, name, blurb }`. */
export const CLASSES = [];

/**
 * One row per weapon, in class order then unlock order.
 *
 * The shape is what `Progression._resolveWeapons` consumes and must not change
 * casually: `{ id, aliases, class, level, fallbackName, note }`. Everything past
 * `note` is extra and is ignored by that consumer.
 */
export const WEAPON_UNLOCKS = [];

/** `id -> row`, for the loadout screen's stat sheet. */
const ROSTER_BY_ID = new Map();

/** Every unlock, weapons and cosmetics together, keyed by the level it lands on. */
const BY_LEVEL = new Map();

/* ---------------------------------------------------------------------------- *
 * Cosmetics. Purely decorative collectibles listed in the loadout screen. They have
 * no gameplay effect and are not pretending to — the point is that the ladder never
 * hands you an empty level, and that the reward text changes shape often enough
 * that levelling does not feel like the same event 54 times.
 *
 * `kind` drives nothing but the badge glyph and the grouping in the UI.
 * ---------------------------------------------------------------------------- */

export const COSMETIC_UNLOCKS = [
  { level: 1, kind: 'callsign', name: 'RECRUIT' },
  { level: 2, kind: 'reticle', name: 'DOT' },
  { level: 3, kind: 'camo', name: 'WOODLAND' },
  { level: 4, kind: 'emblem', name: 'SKULL' },
  { level: 5, kind: 'reticle', name: 'CROSS' },
  { level: 6, kind: 'charm', name: 'DOG TAG' },
  { level: 7, kind: 'camo', name: 'DESERT TAN' },
  { level: 8, kind: 'emblem', name: 'TRIDENT' },
  { level: 9, kind: 'reticle', name: 'CHEVRON' },
  { level: 10, kind: 'callsign', name: 'OPERATOR' },
  { level: 11, kind: 'camo', name: 'URBAN GREY' },
  { level: 12, kind: 'charm', name: 'LOADED DICE' },
  { level: 13, kind: 'emblem', name: 'HOURGLASS' },
  { level: 14, kind: 'reticle', name: 'BRACKET' },
  { level: 15, kind: 'camo', name: 'ARCTIC' },
  { level: 16, kind: 'callsign', name: 'SPECIALIST' },
  { level: 17, kind: 'emblem', name: 'WOLF' },
  { level: 18, kind: 'charm', name: 'SPENT SHELL' },
  { level: 19, kind: 'reticle', name: 'PIP' },
  { level: 20, kind: 'camo', name: 'TIGER STRIPE' },
  { level: 21, kind: 'emblem', name: 'ANVIL' },
  { level: 22, kind: 'callsign', name: 'VETERAN' },
  { level: 23, kind: 'charm', name: 'BRASS PIN' },
  { level: 24, kind: 'reticle', name: 'HALO' },
  { level: 25, kind: 'camo', name: 'DIGITAL ASH' },
  { level: 26, kind: 'emblem', name: 'RAVEN' },
  { level: 27, kind: 'charm', name: 'BENT KEY' },
  { level: 28, kind: 'reticle', name: 'TRIANGLE' },
  { level: 29, kind: 'camo', name: 'OXIDE' },
  { level: 30, kind: 'callsign', name: 'GHOST' },
  { level: 31, kind: 'emblem', name: 'COMPASS ROSE' },
  { level: 32, kind: 'charm', name: 'CHALLENGE COIN' },
  { level: 33, kind: 'reticle', name: 'DUPLEX' },
  { level: 34, kind: 'camo', name: 'SPLINTER' },
  { level: 35, kind: 'emblem', name: 'LANTERN' },
  { level: 36, kind: 'callsign', name: 'REAPER' },
  { level: 37, kind: 'reticle', name: 'MIL-DOT' },
  { level: 38, kind: 'camo', name: 'NIGHTFALL' },
  { level: 39, kind: 'emblem', name: 'SPADE' },
  { level: 40, kind: 'charm', name: 'IVORY DIE' },
  { level: 41, kind: 'reticle', name: 'HORSESHOE' },
  { level: 42, kind: 'camo', name: 'CARBON' },
  { level: 43, kind: 'emblem', name: 'BOLT' },
  { level: 44, kind: 'callsign', name: 'WARDEN' },
  { level: 45, kind: 'reticle', name: 'DELTA' },
  { level: 46, kind: 'camo', name: 'GRAPHITE' },
  { level: 47, kind: 'emblem', name: 'SERPENT' },
  { level: 48, kind: 'charm', name: 'STEEL TAG' },
  { level: 49, kind: 'reticle', name: 'APERTURE' },
  { level: 50, kind: 'callsign', name: 'PHANTOM' },
  { level: 51, kind: 'camo', name: 'BLACKOUT' },
  { level: 52, kind: 'emblem', name: 'PHOENIX' },
  { level: 53, kind: 'reticle', name: 'PRISM' },
  { level: 54, kind: 'callsign', name: 'VANGUARD' },
  { level: 55, kind: 'camo', name: 'GOLD', note: 'MAX RANK' },
  { level: 55, kind: 'emblem', name: 'BLACK CROWN', note: 'MAX RANK' },
];

/** Short badge glyphs. Text, not images — the game ships no binary assets. */
export const KIND_GLYPH = {
  weapon: '▰',
  camo: '▦',
  reticle: '◎',
  emblem: '◈',
  callsign: '▸',
  charm: '●',
};

export const KIND_LABEL = {
  weapon: 'WEAPON',
  camo: 'CAMO',
  reticle: 'RETICLE',
  emblem: 'EMBLEM',
  callsign: 'CALLSIGN',
  charm: 'CHARM',
};

/* ---------------------------------------------------------------------------- *
 * Derivation.
 * ---------------------------------------------------------------------------- */

/** Whatever module currently supplies weapon definitions. Swappable for tests. */
let SOURCE = WEAPON_DATA;

/**
 * Point the ladder at a different weapon module and rebuild.
 *
 * Only the debug harness calls this. It exists because the roster is data that
 * arrives from a file this feature does not own, and being able to substitute a
 * fixture is the difference between testing the screen and testing today's dataset.
 */
export function setWeaponSource(source) {
  SOURCE = source ?? WEAPON_DATA;
  rebuild();
  return WEAPON_UNLOCKS.length;
}

/** The raw `WEAPONS` map from the current source, or `{}`. */
export function weaponDefs() {
  const defs = SOURCE?.WEAPONS;
  return defs && typeof defs === 'object' ? defs : {};
}

/** The definition for `id`, or null. Never throws. */
export function weaponDef(id) {
  return weaponDefs()[id] ?? null;
}

/**
 * The ladder row for `id`: `{ id, class, level, rank, name, className, ttk, def }`.
 * Null for an id that is not on the roster.
 */
export function weaponRow(id) {
  return ROSTER_BY_ID.get(id) ?? null;
}

/** Every roster row, in class order then unlock order. */
export function weaponRoster() {
  return WEAPON_UNLOCKS;
}

function classList() {
  const raw = SOURCE?.WEAPON_CLASSES;
  const meta = new Map(CLASS_META.map((c) => [c.id, c]));
  if (Array.isArray(raw) && raw.length) {
    // The weapon data owns the set and the order; we own the copy.
    return raw
      .map((c) => {
        const id = String(c?.id ?? c?.classId ?? '').toLowerCase();
        if (!id) return null;
        const m = meta.get(id);
        return {
          id,
          label: c?.label ?? c?.short ?? c?.tag ?? m?.label ?? id.toUpperCase(),
          name: c?.name ?? c?.title ?? m?.name ?? id.toUpperCase(),
          blurb: m?.blurb ?? c?.blurb ?? c?.desc ?? '',
        };
      })
      .filter(Boolean);
  }
  return CLASS_META.map((c) => ({ ...c }));
}

/**
 * Every def the source publishes, in the order it wants them.
 *
 * `weaponsByClass()` is the preferred entry point because it carries the source's own
 * intra-class ordering. It may hand back a plain object, a Map, or an array of
 * `{ classId, weapons }` groups, so all three are unpacked. Falling back to the raw
 * `WEAPONS` map costs only the ordering, which `rebuild` re-imposes anyway.
 */
function sourceDefs() {
  const out = [];
  const seen = new Set();
  const take = (def) => {
    if (!def || typeof def !== 'object') return;
    const id = def.id;
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(def);
  };

  const defs0 = weaponDefs();
  try {
    const grouped = SOURCE?.weaponsByClass?.();
    if (grouped) {
      const groups =
        grouped instanceof Map
          ? [...grouped.values()]
          : Array.isArray(grouped)
            ? grouped.map((g) => (Array.isArray(g) ? g : (g?.weapons ?? [])))
            : Object.values(grouped);
      // Groups may hold ids or whole defs; the shipping module hands back ids.
      for (const g of groups) for (const d of g ?? []) take(typeof d === 'string' ? defs0[d] : d);
    }
  } catch (err) {
    console.warn('[unlocks] weaponsByClass() failed; falling back to WEAPONS', err);
  }

  // Belt and braces: anything the grouping missed still reaches the loadout screen.
  const defs = weaponDefs();
  for (const key of Object.keys(defs)) {
    const def = defs[key];
    if (def && typeof def === 'object' && !def.id) take({ ...def, id: key });
    else take(def);
  }
  return out;
}

function classIdFor(def) {
  const explicit = String(def?.classId ?? '').toLowerCase();
  if (explicit) return explicit;
  const byId = CLASS_BY_ID[def?.id];
  if (byId) return byId;
  const name = String(def?.className ?? '');
  for (const [re, id] of CLASS_BY_NAME) if (re.test(name)) return id;
  return 'ar';
}

/**
 * `unlockLevel` normalised. `0`, `null`, `'initial'` and anything unparseable all
 * mean initial issue; a number is clamped into the ladder.
 */
function levelFor(def) {
  const raw = def?.unlockLevel ?? def?.unlock?.level ?? def?.level;
  if (raw == null) return LEVEL_INITIAL;
  if (typeof raw === 'string') {
    if (/^(initial|start|default|base|none)$/i.test(raw.trim())) return LEVEL_INITIAL;
    const n = Number(raw);
    if (!Number.isFinite(n)) return LEVEL_INITIAL;
    return clampLevel(n);
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 1) return n === 1 ? 1 : LEVEL_INITIAL;
  return clampLevel(n);
}

function clampLevel(n) {
  return Math.min(MAX_LEVEL, Math.max(LEVEL_INITIAL, Math.round(n)));
}

/**
 * Seconds, whatever unit the source used. Values above 20 are read as milliseconds.
 *
 * Zero is a value, not a gap: the reference prints `0` for a weapon that kills in one
 * shot, and turning that into "no data" would hide the single most important fact
 * about a sniper rifle. Only a negative or unparseable figure is missing.
 */
export function seconds(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return n > 20 ? n / 1000 : n;
}

function rebuild() {
  const classes = classList();
  CLASSES.length = 0;
  CLASSES.push(...classes);
  const order = new Map(classes.map((c, i) => [c.id, i]));

  const rows = sourceDefs().map((def, i) => {
    let cls = classIdFor(def);
    if (!order.has(cls)) cls = classes[0]?.id ?? 'ar';
    const level = levelFor(def);
    return {
      id: def.id,
      // `Progression._resolveWeapons` walks `[id, ...aliases]` against the weapon
      // map. The id is exact now, so the alias list is a formality kept for shape.
      aliases: [def.id],
      class: cls,
      classId: cls,
      level,
      fallbackName: def.name ?? String(def.id ?? '').toUpperCase(),
      name: def.name ?? String(def.id ?? '').toUpperCase(),
      className: def.className ?? classes[order.get(cls) ?? 0]?.name ?? '',
      rank: typeof def.rank === 'string' && def.rank ? def.rank : null,
      ttk: def.ttk && typeof def.ttk === 'object' ? def.ttk : null,
      note: level === LEVEL_INITIAL ? 'STANDARD ISSUE' : undefined,
      def,
      _i: i,
    };
  });

  // Class order first, then the ladder within a class, then the source's own order.
  // A player reading a class column reads it in the order they will earn it.
  rows.sort(
    (a, b) =>
      (order.get(a.class) ?? 99) - (order.get(b.class) ?? 99) ||
      a.level - b.level ||
      a._i - b._i
  );

  WEAPON_UNLOCKS.length = 0;
  WEAPON_UNLOCKS.push(...rows);
  ROSTER_BY_ID.clear();
  for (const r of rows) ROSTER_BY_ID.set(r.id, r);

  BY_LEVEL.clear();
  for (const w of rows) {
    // Initial issue is not an unlock — it is what you already have — so it does not
    // go on the ladder at all. Putting it on level 1 would make the first level-up
    // claim to hand you six guns you have been holding since the boot screen.
    if (w.level === LEVEL_INITIAL) continue;
    push(w.level, {
      kind: 'weapon',
      weaponId: w.id,
      // Deliberately NOT `classId`. `Progression._decorate` rewrites a weapon
      // entry's name and id from `weapons.find(x => x.classId === entry.classId)`,
      // which was correct when a class held exactly one gun and picks the wrong
      // weapon now that a class holds six. With no `classId` on the entry that
      // lookup finds nothing and the already-correct name and id survive intact.
      clsId: w.class,
      level: w.level,
      name: w.name,
      rank: w.rank,
      note: w.note,
      unbuilt: false,
    });
  }
  for (const c of COSMETIC_UNLOCKS) {
    push(c.level, { kind: c.kind, level: c.level, name: c.name, note: c.note });
  }

  // Weapons first within a level: the gun is the headline, the camo is the footnote.
  for (const list of BY_LEVEL.values()) {
    list.sort((a, b) => (a.kind === 'weapon' ? 0 : 1) - (b.kind === 'weapon' ? 0 : 1));
  }
}

function push(level, entry) {
  let list = BY_LEVEL.get(level);
  if (!list) BY_LEVEL.set(level, (list = []));
  list.push(entry);
}

rebuild();

/* ---------------------------------------------------------------------------- *
 * Lookups.
 * ---------------------------------------------------------------------------- */

/** Everything unlocked exactly at `level`. Never null; safe for any input. */
export function unlocksAtLevel(level) {
  return BY_LEVEL.get(level) ?? [];
}

/** The first level above `level` that unlocks something, or null past the cap. */
export function nextUnlockLevel(level) {
  for (let l = level + 1; l <= MAX_LEVEL; l++) if (BY_LEVEL.has(l)) return l;
  return null;
}

/** Level at which `weaponId` becomes available, or null if it is not on the table. */
export function weaponUnlockLevel(weaponId) {
  const w = ROSTER_BY_ID.get(weaponId);
  return w ? w.level : null;
}

/** Every level that hands out at least one weapon, ascending. */
export function weaponUnlockLevels() {
  return [...new Set(WEAPON_UNLOCKS.map((w) => w.level).filter((l) => l !== LEVEL_INITIAL))].sort(
    (a, b) => a - b
  );
}

/* ---------------------------------------------------------------------------- *
 * Provenance.
 *
 * The stat sheet prints a mark beside every number saying where it came from, and
 * the weapon dataset already knows: it assembles each weapon from three labelled
 * blocks. `SCRAPED` is verbatim from the reference, `DERIVED` is arithmetic on top of
 * it, `AUTHORED` (with `CLASS_BASE` under it) is invented for this game. Reading the
 * blocks is the only way for the mark to stay true after someone retunes a weapon.
 * ---------------------------------------------------------------------------- */

/**
 * Enemy health and the head hitbox the AI module applies. Read from the weapon stats
 * module — which records them precisely so that nothing has to hardcode them twice —
 * with the same values as a fallback so a reshaped module cannot produce NaN.
 */
export const COMBAT = {
  get health() {
    return num(WEAPON_STATS?.ENEMY_HP, 100);
  },
  get headHitbox() {
    return num(WEAPON_STATS?.ENEMY_HEAD_HITBOX, 3.0);
  },
};

function num(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Everything the stat sheet needs beyond the assembled definition: the verbatim
 * source row (published damage bands, TTK, rank, magazine, and the page it came
 * from), the derived block, and the authored block.
 *
 * Every field is optional. A weapon the stats module has never heard of returns all
 * nulls and the sheet falls back to marking its numbers "assumed".
 */
export function weaponFacts(id) {
  const def = weaponDef(id);
  const scraped = WEAPON_STATS?.SCRAPED?.[id] ?? null;
  const derived = WEAPON_STATS?.DERIVED?.[id] ?? null;
  const authored = WEAPON_STATS?.AUTHORED?.[id] ?? null;
  const classBase = def?.classId ? (WEAPON_STATS?.CLASS_BASE?.[def.classId] ?? null) : null;
  return {
    def,
    scraped,
    derived,
    authored,
    classBase,
    /** The effective head multiplier this weapon was reconciled onto, if known. */
    headMult: Number(WEAPON_STATS?.HEAD_MULT?.[id]) || null,
    /** True when the three blocks were readable at all. */
    known: !!(scraped || derived || authored),
  };
}

/**
 * Which block a stat sheet field came from: `'src'`, `'calc'`, `'game'`, or null when
 * the dataset does not account for it and the caller should say so.
 *
 * The keys are the sheet's, not the data's — one sheet row can be backed by a whole
 * sub-object (every cone angle is `AUTHORED.spread`), and the mapping is the only
 * place that correspondence is written down.
 */
const FIELD_ORIGIN = {
  damage: ['scraped', 'bands'],
  ttk: ['scraped', 'ttk'],
  rank: ['scraped', 'rank'],
  magSize: ['scraped', 'magSize'],
  unlock: ['scraped', 'unlock'],
  rpm: ['derived', 'rpm'],
  curve: ['derived', 'curve'],
  reserve: ['derived', 'startReserve'],
  headMult: ['derived', 'headHitboxScale'],
  fireMode: ['authored', 'fireMode'],
  reload: ['authored', 'reload'],
  ads: ['authored', 'ads'],
  sprintOut: ['authored', 'ergonomics'],
  switch: ['authored', 'ergonomics'],
  spread: ['authored', 'spread'],
  bloom: ['authored', 'spread'],
  recoil: ['authored', 'recoil'],
  penetration: ['authored', 'penetration'],
  muzzleVelocity: ['authored', 'muzzleVelocity'],
  pellets: ['authored', 'pellets'],
  burst: ['authored', 'burstCount'],
};

const BLOCK_MARK = { scraped: 'src', derived: 'calc', authored: 'game' };

export function fieldOrigin(facts, key) {
  const entry = FIELD_ORIGIN[key];
  if (!entry || !facts?.known) return null;
  const [block] = entry;
  // An authored field the weapon does not override comes from its class archetype,
  // which is authored just the same — so the block, not the key, is what decides.
  if (block === 'authored' && !facts.authored && !facts.classBase) return null;
  if (block !== 'authored' && !facts[block]) return null;
  return BLOCK_MARK[block];
}

/** The reference page a weapon's published numbers were taken from, if recorded. */
export function sourcePage(id) {
  const p = WEAPON_STATS?.SCRAPED?.[id]?.page;
  return typeof p === 'string' ? p : null;
}

/**
 * Levels between 1 and the cap that unlock nothing at all. Should always be empty —
 * the loadout screen asserts on it in the debug harness rather than in the frame
 * path, because "every level carries an unlock" is a design rule that only a data
 * edit can break.
 */
export function emptyLevels() {
  const out = [];
  for (let l = 1; l <= MAX_LEVEL; l++) if (!BY_LEVEL.has(l)) out.push(l);
  return out;
}

/* ============================================================================ *
 * WEAPON LEVEL — the second ladder.
 *
 * There are two levels in this game and conflating them produces a screen that
 * lies to the player:
 *
 *   - **career level** (everything above): 1..55, one per account, earned from every
 *     kill with every gun, and what gates whether a *weapon* is yours at all.
 *   - **weapon level** (everything below): 1..40, one per weapon, earned only while
 *     that weapon is in your hands, and what gates the *parts* you can bolt onto it.
 *
 * A player at career 55 with a gun they picked up this morning is level 55 and weapon
 * level 1 simultaneously, and both of those facts are true and useful. So the curves,
 * the caps and the storage are deliberately separate; nothing below reads `MAX_LEVEL`
 * or `CUMULATIVE`, and nothing above reads any of this.
 *
 * The *state* — which weapon is at what level — belongs to the attachment system,
 * which is being authored concurrently. This file owns only the shape of the ladder:
 * how much a weapon level costs, what the slots are called, and what reaching 40
 * means. That is the same division as the career ladder, where `Progression` holds
 * the XP and this file answers "what does level N give me".
 * ============================================================================ */

export const WEAPON_MAX_LEVEL = 40;

/**
 * The *shape* of the weapon ladder, and nothing about its state.
 *
 * The curve, the XP and every weapon's current level live in
 * `src/weapons/attachments/`, which owns them. A second copy of the curve here would
 * be exactly the drift generator this file's own header warns about — two files
 * answering "what does weapon level 12 cost" and disagreeing after one edit — so
 * there is no copy. What is below is the vocabulary the Gunsmith screen needs to draw
 * the ladder: what the slots are called, how many points a build gets, and what
 * reaching the top means.
 */

/** A weapon-level progress shape with nothing in it, for a screen with no system behind it. */
export function weaponProgress() {
  return { xp: 0, level: 1, into: 0, need: 0, fraction: 0, maxed: false };
}

/* ---------------------------------------------------------------------------- *
 * Slots.
 *
 * Eleven named positions on a weapon. Not every weapon carries every one — a
 * shell-fed shotgun has no magazine to swap and a bolt gun has no trigger group to
 * re-time — and *which* it carries is a question the attachment system answers.
 * This is only the vocabulary and the order they are shown in, which runs roughly
 * front-to-back along the weapon so the rail reads like the gun does.
 * ---------------------------------------------------------------------------- */

export const ATTACHMENT_SLOTS = [
  'optic',
  'muzzle',
  'barrel',
  'underbarrel',
  'laser',
  'magazine',
  'fireMod',
  'rearGrip',
  'stock',
  'stockPad',
  'comb',
];

/** Display names. Descriptive rather than branded — these are positions, not products. */
export const SLOT_LABEL = {
  optic: 'SIGHT',
  muzzle: 'MUZZLE DEVICE',
  barrel: 'BARREL',
  underbarrel: 'UNDER-RAIL',
  laser: 'ILLUMINATOR',
  magazine: 'FEED',
  fireMod: 'TRIGGER GROUP',
  rearGrip: 'GRIP',
  stock: 'STOCK',
  stockPad: 'RECOIL PAD',
  comb: 'CHEEK RISER',
};

/** Two-letter tags for the rail, matching the class rail's shape. */
export const SLOT_TAG = {
  optic: 'SI',
  muzzle: 'MZ',
  barrel: 'BR',
  underbarrel: 'UR',
  laser: 'IL',
  magazine: 'FD',
  fireMod: 'TG',
  rearGrip: 'GR',
  stock: 'ST',
  stockPad: 'PD',
  comb: 'CK',
};

/**
 * How large a build is.
 *
 * A budget in points, not a count of parts: a part costs one or two, so five points
 * buys five ordinary parts or three with two heavy ones among them, and *which five*
 * becomes the decision rather than merely *whether five*.
 *
 * The attachment system is authoritative for both numbers and for when the expanded
 * limit is earned — it exposes `limitFor(weaponId)` and a `setExpansionCondition`
 * seam that is deliberately still unset. These are here so a screen with no system
 * behind it draws a plausible budget instead of a zero, and so the copy on the header
 * has a number to name.
 */
export const FIT_LIMIT = { base: 5, expanded: 8 };

/**
 * Reaching weapon level 40.
 *
 * Deliberately not a camo. The career ladder's level-55 reward is GOLD, and handing
 * out a second paint job would make the two ladders look like the same ladder. The
 * attachment system gives every weapon its own mastery *part* at 40 — a one-off
 * conversion in a slot chosen per weapon — and this is the label the screen puts on
 * that moment when the part itself cannot be named.
 */
export const WEAPON_MASTERY = {
  level: WEAPON_MAX_LEVEL,
  kind: 'mastery',
  name: 'ORDNANCE SEAL',
  note: 'EVERY PART UNLOCKED · RECEIVER INSCRIBED',
};

KIND_GLYPH.mastery = '✶';
KIND_LABEL.mastery = 'MASTERY';
KIND_GLYPH.attachment = '▣';
KIND_LABEL.attachment = 'ATTACHMENT';
